import React, { useState, useEffect, useMemo, useRef } from 'react'
import PropTypes from 'prop-types'
import { useQuery } from '@tanstack/react-query'
import { useAtom } from 'jotai'
import { loadingAtom } from '../atoms'
import { useForm, Controller } from 'react-hook-form'
import { toast } from 'react-toastify'
import { apiFetch } from '../utils/apiUtils'

// Storage key for passport file only
const PASSPORT_FILE_KEY = 'flight_registration_passport_file';

// FILE-UPLOAD LIMITS
const MAX_SIZE_BYTES = 25 * 1024 ** 2; // 25 MB per file

// Safe storage wrapper to handle localStorage errors
const safeStorage = {
  getItem: (key) => {
    try {
      return localStorage.getItem(key);
    } catch (err) {
      console.error(`Error reading ${key} from localStorage:`, err);
      return null;
    }
  },
  setItem: (key, value) => {
    try {
      localStorage.setItem(key, value);
      return true;
    } catch (err) {
      console.error(`Error writing ${key} to localStorage:`, err);
      return false;
    }
  },
  removeItem: (key) => {
    try {
      localStorage.removeItem(key);
      return true;
    } catch (err) {
      console.error(`Error removing ${key} from localStorage:`, err);
      return false;
    }
  }
};

const TERMS_AND_CONDITIONS = `Terms & Conditions

By reserving a seat on this charter flight, the passenger acknowledges and agrees to the following terms:

Payment & Refund Policy
All payments are final and non-refundable. Due to the nature of charter arrangements and advance commitments made with airlines and service providers, no refunds will be issued for cancellations, missed departures, or changes in travel plans.

Role of the Organizations
Eretz Hakodesh and the Igud are assisting in coordinating this travel option at the request of Mosdos and students. The flight and related travel services are operated and arranged by Plan It Rite and its travel partners. Accordingly, the above organizations do not operate the flight or transportation services and assume no responsibility or liability for the operation of the flight or related travel services.

Travel Risks & Liability
Passengers travel at their own risk. The coordinating organizations shall not be held responsible for any delays, cancellations, schedule changes, missed connections, border crossing issues, baggage loss or damage, personal injury, illness, or any other incident that may occur during the journey.

Border Crossing & Travel Requirements
Passengers are responsible for ensuring that they have valid travel documents, visas, and any other required documentation for border crossings and international travel. Any issues arising from documentation or entry requirements are the sole responsibility of the passenger.

Baggage & Personal Property
Passengers are responsible for their own baggage and personal belongings throughout the journey, including during the border crossing. The coordinating organizations assume no responsibility for lost, stolen, or damaged items.

Schedule Changes
Travel times and arrangements may be subject to change due to operational, security, or logistical considerations. Passengers agree to comply with instructions provided by the organizers and travel operators.`;

// Collapsible section component
const CollapsibleSection = ({ title, defaultOpen = false, children }) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <span className="text-lg font-semibold text-gray-800">{title}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transform transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 py-4">
          {children}
        </div>
      )}
    </div>
  );
};

const TermsModal = ({ isOpen, onClose }) => {
  if (!isOpen) return null;
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[80vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold text-gray-800">Terms & Conditions</h2>
          <button type="button" onClick={onClose} className="text-gray-500 hover:text-gray-700 text-2xl leading-none">&times;</button>
        </div>
        <div className="text-gray-700 text-sm whitespace-pre-line">{TERMS_AND_CONDITIONS}</div>
        <div className="mt-6 text-center">
          <button type="button" onClick={onClose} className="px-6 py-2 bg-logo-blue text-white rounded-lg hover:bg-blue-700">Close</button>
        </div>
      </div>
    </div>
  );
};

const FlightRegistrationForm = ({ onSubmitSuccess }) => {
  const [showTermsModal, setShowTermsModal] = useState(false);

  // Detect access code from URL query parameter
  const accessCode = useMemo(() => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('code')?.trim().toUpperCase() || null;
  }, []);

  // Validate access code and get restrictions (flight/route)
  const { data: codeInfo } = useQuery({
    queryKey: ['accessCodeCheck', accessCode],
    queryFn: async () => {
      const res = await fetch(`${import.meta.env.VITE_API_URL || '/api'}/check-access-code?code=${encodeURIComponent(accessCode)}`);
      return res.json();
    },
    enabled: !!accessCode,
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const codeAllowedFlightId = codeInfo?.valid ? codeInfo.allowedFlightId || null : null;
  const codeAllowedRoute = codeInfo?.valid ? codeInfo.allowedRoute || null : null;

  // Fetch schools using TanStack Query (skip if using access code)
  const {
    data: schoolsData,
    isLoading: schoolsLoading,
    error: schoolsError
  } = useQuery({
    queryKey: ['schools'],
    queryFn: async () => {
      const apiUrl = `${import.meta.env.VITE_API_URL || '/api'}/schools`;
      const result = await apiFetch(apiUrl, { method: 'GET' });

      if (result.status === 'success') {
        return [
          { value: '', label: 'Select your school...' },
          ...result.schools.map(school => ({
            value: school.id,
            label: school.name
          })),
          { value: 'chaim-vchessed', label: "Chaim V'Chessed" },
          { value: '__other__', label: 'Other / N/A' }
        ];
      } else {
        throw new Error('Failed to load schools');
      }
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: 2,
    enabled: true
  });

  // Fetch capacity info (including NYC availability)
  const { data: capacityData } = useQuery({
    queryKey: ['capacity'],
    queryFn: async () => {
      const apiUrl = `${import.meta.env.VITE_API_URL || '/api'}/capacity`;
      const res = await fetch(apiUrl);
      return res.json();
    },
    staleTime: 60 * 1000, // 1 minute
    retry: 2,
  });
  // Derive per-flight capacity from the new flights map returned by the API
  const flightsMap = capacityData?.flights || {};
  // Find all open flights that aren't full
  const openFlights = Object.entries(flightsMap).filter(([, f]) => f.isOpen);
  // Default to the first open flight (or code-forced flight)
  const [selectedFlightId, setSelectedFlightId] = useState(() => {
    const open = Object.entries(flightsMap).find(([, f]) => f.isOpen);
    return open ? open[0] : null;
  });
  // Update selectedFlightId when capacity data or code info loads
  useEffect(() => {
    // Access code forces a specific flight (even if closed)
    if (codeAllowedFlightId && flightsMap[codeAllowedFlightId]) {
      setSelectedFlightId(codeAllowedFlightId);
    } else if (!selectedFlightId && openFlights.length > 0) {
      setSelectedFlightId(openFlights[0][0]);
    }
  }, [capacityData, codeAllowedFlightId]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedFlightData = selectedFlightId ? flightsMap[selectedFlightId] : null;
  const isNycFull = selectedFlightData?.isNycFull || false;
  const isMilanFull = selectedFlightData?.isMilanFull || false;
  const isInfantFull = selectedFlightData?.isInfantFull || false;

  // Access code users bypass public capacity; code-forced flight users also bypass
  const isPublicFull = accessCode ? false : (selectedFlightData ? selectedFlightData.isPublicFull : true);

  // Check if ALL flights are closed or full (for the "all flights full" banner)
  const allFlightsFull = !accessCode && (openFlights.length === 0 || openFlights.every(([, f]) => f.isPublicFull));

  // Build vertical list of all flight+route combos, ordered by departure date
  const flightRouteOptions = useMemo(() => {
    if (accessCode) return []; // Access code users use the old flow
    const options = [];
    // Sort flights by departJerusalemDay date
    const sortedFlights = [...openFlights].sort((a, b) => {
      const dateA = new Date(a[1].departJerusalemDay + ', 2026');
      const dateB = new Date(b[1].departJerusalemDay + ', 2026');
      return dateA - dateB;
    });
    for (const [fid, fdata] of sortedFlights) {
      const milanPrice = fdata.routes?.['jerusalem-milan']?.price;
      const nycPrice = fdata.routes?.['jerusalem-newyork']?.price;
      // Find the earliest cancel deadline for this flight as booking cutoff
      const deadlines = fdata.cancelDeadlines || {};
      if (milanPrice != null) {
        options.push({
          flightId: fid,
          route: 'jerusalem-milan',
          label: fdata.label,
          routeLabel: `Jerusalem → ${fdata.arrivalCity || 'Milan (MXP)'}`,
          price: milanPrice,
          soldOut: fdata.isMilanFull || fdata.isPublicFull,
          cutoff: deadlines['jerusalem-milan'] || null,
          departDay: fdata.departJerusalemDay,
          arrivalCity: fdata.arrivalCity,
          connectionWarning: fdata.connectionWarning,
        });
      }
      if (nycPrice != null) {
        options.push({
          flightId: fid,
          route: 'jerusalem-newyork',
          label: fdata.label,
          routeLabel: `Jerusalem → New York`,
          routeSub: `via ${fdata.arrivalCity || 'Milan'}`,
          price: nycPrice,
          soldOut: fdata.isNycFull || fdata.isPublicFull,
          cutoff: deadlines['jerusalem-newyork'] || null,
          departDay: fdata.departJerusalemDay,
          arrivalCity: fdata.arrivalCity,
          connectionWarning: fdata.connectionWarning,
        });
      }
    }
    return options;
  }, [openFlights, accessCode]);

  // Normalize code route restriction for comparison
  const normalizedCodeRoute = codeAllowedRoute
    ? (codeAllowedRoute.includes('milan') ? 'jerusalem-milan' : codeAllowedRoute.includes('newyork') || codeAllowedRoute.includes('nyc') ? 'jerusalem-newyork' : codeAllowedRoute)
    : null;

  // Use schools from query or fallback
  const schools = schoolsData || [{ value: '', label: 'Select your school...' }];

  // Get empty default values
  const getEmptyDefaultValues = () => {
    return {
      route: '',
      firstName: '',
      lastName: '',
      birthDate: '',
      email: '',
      phone: '',
      passportNumber: '',
      passportCountry: '',
      passportExpiration: '',
      gender: '',
      school: '',
      schoolOther: '',
      termsAccepted: false,
      commitToBook: false,
      interestedSharmElSheikh: false,
      hasInfant: false,
      infantFirstName: '',
      infantLastName: '',
      infantBirthDate: '',
      infantGender: '',
      infantPassportNumber: '',
      infantPassportCountry: '',
      infantPassportExpiration: '',
    };
  };

  // Parse URL query parameters for school only
  const getDefaultValues = () => {
    const urlParams = new URLSearchParams(window.location.search);
    const schoolParam = urlParams.get('school');
    const codeParam = urlParams.get('code');

    const defaultValues = getEmptyDefaultValues();

    // If access code is present, set school to N/A (school is required but not relevant for org-allocated seats)
    if (codeParam) {
      defaultValues.school = 'N/A';
      console.log(`[INFO] URL parameter - code: ${codeParam}, setting school to N/A`);
    } else if (schoolParam) {
      defaultValues.school = schoolParam.trim();
      console.log(`[INFO] URL parameter - school: ${defaultValues.school}`);
    }

    return defaultValues;
  };

  // React Hook Form setup with default values
  const {
    register,
    handleSubmit: hookFormSubmit,
    control,
    reset,
    watch,
    setValue,
    formState
  } = useForm({
    defaultValues: getDefaultValues()
  });

  // Destructure what we need from formState
  const { errors, isSubmitting, isDirty, isSubmitted } = formState;

  // Watch fields for conditional rendering
  const watchSchool = watch('school');
  const watchRoute = watch('route');
  const watchHasInfant = watch('hasInfant');

  // Auto-select route when code restricts it
  useEffect(() => {
    if (normalizedCodeRoute && watchRoute !== normalizedCodeRoute) {
      setValue('route', normalizedCodeRoute);
    }
  }, [normalizedCodeRoute]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use useRef instead of useState for large file objects to avoid unnecessary re-renders
  const passportFileRef = useRef(null);
  const [passportFileName, setPassportFileName] = useState('');

  // Use Jotai atoms for state management
  const [loading, setLoading] = useAtom(loadingAtom);

  // Clear stale passport file name from localStorage on mount
  // (the actual file data is lost on refresh, so showing the old name is misleading)
  useEffect(() => {
    safeStorage.removeItem(PASSPORT_FILE_KEY);
  }, []);

  // Handle passport file upload
  const handlePassportFileChange = (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    const file = e.target.files[0];

    // Validate file size
    if (file.size > MAX_SIZE_BYTES) {
      toast.error(`File must be less than 25 MB.`);
      return;
    }

    // Store the file
    passportFileRef.current = file;
    setPassportFileName(file.name);
    safeStorage.setItem(PASSPORT_FILE_KEY, file.name);
  };

  // Helper function to sanitize text input by removing problematic Unicode characters
  const sanitizeText = (text) => {
    if (typeof text !== 'string') return text;

    return text
      .replace(/[\u202a-\u202e]/g, '')
      .replace(/[\u200b-\u200f]/g, '')
      .replace(/[\ufeff]/g, '')
      .replace(/[\u2060-\u206f]/g, '')
      .trim();
  };

  // Helper function to recursively sanitize all string values in an object
  const sanitizeFormData = (obj) => {
    const sanitized = {};

    for (const [key, value] of Object.entries(obj)) {
      if (typeof value === 'string') {
        sanitized[key] = sanitizeText(value);
      } else if (value && typeof value === 'object' && !Array.isArray(value) && !(value instanceof Date)) {
        sanitized[key] = sanitizeFormData(value);
      } else {
        sanitized[key] = value;
      }
    }

    return sanitized;
  };

  const onSubmit = async (data) => {
    // Sanitize all form data to remove problematic Unicode characters
    const sanitizedData = sanitizeFormData(data);

    // Validate passport file
    if (!passportFileRef.current) {
      toast.error('Please upload your passport');
      return;
    }

    // Show loading overlay
    setLoading(true);

    try {
      // birthDate is already in DD/MM/YYYY format from the text input
      const formattedBirthDate = sanitizedData.birthDate;

      // Prepare form data for multipart submission
      const formData = new FormData();

      // Add all form fields
      formData.append('route', sanitizedData.route);
      formData.append('firstName', sanitizedData.firstName);
      formData.append('lastName', sanitizedData.lastName);
      formData.append('birthDate', formattedBirthDate);
      formData.append('email', sanitizedData.email);
      formData.append('phone', sanitizedData.phone);
      formData.append('passportNumber', sanitizedData.passportNumber);
      formData.append('passportCountry', sanitizedData.passportCountry);
      formData.append('passportExpiration', sanitizedData.passportExpiration);
      formData.append('gender', sanitizedData.gender);

      // Resolve school value: if "Other", use the free-text value
      const schoolValue = sanitizedData.school === '__other__'
        ? sanitizedData.schoolOther
        : sanitizedData.school;
      formData.append('school', schoolValue);
      formData.append('termsAccepted', 'true');
      if (sanitizedData.commitToBook) {
        formData.append('commitToBook', 'true');
      }
      if (sanitizedData.interestedSharmElSheikh) {
        formData.append('interestedSharmElSheikh', 'true');
      }

      // Flight ID
      if (selectedFlightId) {
        formData.append('flightId', selectedFlightId);
      }

      // Infant fields
      if (sanitizedData.hasInfant) {
        formData.append('hasInfant', 'true');
        formData.append('infantFirstName', sanitizedData.infantFirstName);
        formData.append('infantLastName', sanitizedData.infantLastName);
        formData.append('infantBirthDate', sanitizedData.infantBirthDate);
        formData.append('infantGender', sanitizedData.infantGender);
        formData.append('infantPassportNumber', sanitizedData.infantPassportNumber);
        formData.append('infantPassportCountry', sanitizedData.infantPassportCountry);
        formData.append('infantPassportExpiration', sanitizedData.infantPassportExpiration);
      }

      // Include access code if present
      if (accessCode) {
        formData.append('accessCode', accessCode);
      }

      // Upload passport via presigned URL (through CloudFront, same domain)
      if (passportFileRef.current) {
        const file = passportFileRef.current;
        const apiBase = import.meta.env.VITE_API_URL || '/api';

        // Step 1: Get presigned upload URL from Lambda
        const uploadRes = await apiFetch(
          `${apiBase}/flight-upload-url?fileName=${encodeURIComponent(file.name)}&contentType=${encodeURIComponent(file.type || 'application/octet-stream')}`
        );

        if (uploadRes.status !== 'success' || !uploadRes.presignedUrl) {
          throw new Error('Failed to get upload URL — please try again');
        }

        // Step 2: PUT file directly to S3 via CloudFront presigned URL
        const putRes = await fetch(uploadRes.presignedUrl, {
          method: 'PUT',
          body: file,
          headers: { 'Content-Type': file.type || 'application/octet-stream' }
        });

        if (!putRes.ok) {
          throw new Error(`Passport upload failed (${putRes.status}) — please try again`);
        }

        // Step 3: Send S3 key (not the file) with the registration form
        formData.append('passportS3Key', uploadRes.s3Key);
        formData.append('passportFileExt', file.name.split('.').pop().toLowerCase());
      }

      // Submit to the registration endpoint
      const apiUrl = `${import.meta.env.VITE_API_URL || '/api'}/flight-register`;
      const result = await apiFetch(apiUrl, {
        method: 'POST',
        body: formData
      });

      console.log('Response:', result);

      toast.success('Thank you! Your registration has been submitted successfully.');

      // Call the onSubmitSuccess callback (include route for downstream pages)
      if (onSubmitSuccess) {
        onSubmitSuccess({ ...result, route: sanitizedData.route });

        // Reset the form after submission
        setTimeout(() => {
          resetForm();
        }, 500);
      }
    } catch (error) {
      console.error('Error:', error);
      const errorMessage = error.message;
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }

  // Helper function to reset the form
  const resetForm = () => {
    reset(getEmptyDefaultValues());

    // Reset passport file input
    passportFileRef.current = null;
    setPassportFileName('');

    // Reset file input
    const passportInput = document.getElementById('passportFile');
    if (passportInput) passportInput.value = '';

    // Clear passport file storage
    safeStorage.removeItem(PASSPORT_FILE_KEY);
  }

  return (
    <div className="space-y-6">
      {/* Last seats banner */}
      {!allFlightsFull && !accessCode && (
        <div className="bg-orange-600 text-white rounded-lg p-6 text-center">
          <h3 className="text-xl font-bold mb-2">Final Availability — No Additional Flights</h3>
          <p className="text-orange-100 text-base">
            These are the last remaining seats. No additional flights will be added. Seats are available on a first-come, first-served basis — your spot is only confirmed once payment is completed.
          </p>
        </div>
      )}

      {/* Non-Refundable Notice */}
      <div className="bg-red-50 border-2 border-red-300 p-5 rounded-lg">
        <p className="text-red-800 font-bold text-center text-lg">All ticket purchases are final and non-refundable.</p>
        <p className="text-red-700 text-center text-sm mt-1">No refunds will be issued for cancellations, missed departures, or changes in travel plans.</p>
      </div>

      {/* Important Instructions */}
      <div className="bg-amber-50 border border-amber-300 p-6 rounded-lg">
        <h3 className="text-lg font-bold text-amber-900 mb-3">Important Instructions</h3>
        <ul className="text-amber-800 space-y-2 list-disc list-inside">
          <li>Please enter all passenger details exactly as they appear on the passport</li>
          <li>We highly recommend uploading a clear copy of your passport to ensure all data is entered correctly</li>
          {!allFlightsFull && <li>Your booking is not confirmed until payment is completed and you receive a confirmation email. <strong>You only have a ticket once you receive the confirmation email.</strong></li>}
        </ul>
      </div>

      {/* Q&A */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-bold text-gray-800 mb-4">Q&A</h3>
        <div className="space-y-4 text-gray-700">
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">What is included?</h4>
            <p>Your booking includes transportation from Jerusalem to the border and to Taba Airport, Israeli and Egyptian border crossing taxes, and a charter flight from Taba to {selectedFlightData?.arrivalCity || 'Milan'}. The NYC option includes a connecting flight from {selectedFlightData?.arrivalCity || 'Milan'} to New York.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">Will food be provided?</h4>
            <p>Light refreshments will be available. Please bring enough food for yourself for the journey.</p>
          </div>
          <div>
            <h4 className="font-semibold text-gray-800 mb-1">What is the baggage policy?</h4>
            <p>Included: 1 checked bag, 1 carry-on, and 1 personal item. <strong>Important:</strong> We strongly recommend packing light, as you will be required to walk with your luggage through the border crossing.</p>
          </div>
        </div>
      </div>

      {/* All flights full banner */}
      {allFlightsFull && (
        <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-6 text-center">
          <h3 className="text-xl font-bold text-blue-800 mb-3">Register Your Interest for the Next Flight</h3>
          <p className="text-blue-700 mb-2">
            All three charter flights have departed successfully. We are gauging interest for an additional flight.
          </p>
          <p className="text-blue-700 mb-2">
            Estimated price: approximately <strong>$1,960 +/- $100</strong> for the NYC route. Final pricing and flight details will depend on the level of interest.
          </p>
          <p className="text-blue-700 font-semibold">
            Please register below and we will contact you by email with details as they become available.
          </p>
        </div>
      )}

      {/* Access code: flight info banner */}
      {!allFlightsFull && codeAllowedFlightId && selectedFlightData && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center">
          <p className="text-blue-800 font-semibold text-lg">{selectedFlightData.label}</p>
          <p className="text-blue-600 text-sm mt-1">Your access code is for this flight</p>
        </div>
      )}

      {/* Access code users with multi-flight codes: show flight selector */}
      {!allFlightsFull && accessCode && !codeAllowedFlightId && openFlights.length > 1 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">Select Your Flight</h3>
          <div className="space-y-3">
            {openFlights.map(([fid, fdata]) => (
              <label key={fid} className={`block cursor-pointer rounded-lg border-2 p-5 transition-all ${selectedFlightId === fid ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200' : 'border-gray-300 bg-white hover:border-blue-300'}`}>
                <input type="radio" name="flightSelector" value={fid} checked={selectedFlightId === fid} onChange={() => setSelectedFlightId(fid)} className="w-4 h-4 text-blue-600 mr-3" />
                <span className="font-bold text-gray-800">{fdata.label}</span>
                <span className="text-sm text-gray-600 ml-2">{fdata.departJerusalemDay} &rarr; {fdata.arrivalCity}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Public users: vertical list of all flight+route options */}
      {!allFlightsFull && !accessCode && flightRouteOptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-2">Select Your Flight & Route</h3>
          <p className="text-sm text-gray-500 mb-4">These are the last available flights. Seats are first-come, first-served.</p>
          <div className="space-y-3">
            {flightRouteOptions.map((opt) => {
              const isSelected = selectedFlightId === opt.flightId && watchRoute === opt.route;
              const cutoffDate = opt.cutoff ? new Date(opt.cutoff) : null;
              const cutoffStr = cutoffDate ? cutoffDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true, timeZone: 'Asia/Jerusalem' }) + ' (Israel)' : null;
              const isPastCutoff = cutoffDate && cutoffDate < new Date();
              return (
                <label
                  key={`${opt.flightId}-${opt.route}`}
                  className={`block rounded-lg border-2 p-5 transition-all ${
                    opt.soldOut
                      ? 'border-gray-200 bg-gray-50 cursor-not-allowed opacity-60'
                      : isSelected
                        ? 'border-blue-500 bg-blue-50 ring-2 ring-blue-200 cursor-pointer'
                        : 'border-gray-200 bg-white hover:border-blue-300 cursor-pointer'
                  }`}
                  onClick={(e) => {
                    if (opt.soldOut) { e.preventDefault(); return; }
                    setSelectedFlightId(opt.flightId);
                    setValue('route', opt.route);
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <input
                        type="radio"
                        name="flightRouteSelector"
                        checked={isSelected}
                        disabled={opt.soldOut}
                        onChange={() => {}}
                        className="w-5 h-5 text-blue-600"
                      />
                      <div>
                        <div className="font-bold text-gray-800">{opt.routeLabel}</div>
                        {opt.routeSub && <div className="text-sm text-gray-500">{opt.routeSub}</div>}
                        <div className="text-sm text-gray-600 mt-1">{opt.label}</div>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className={`text-2xl font-bold ${opt.soldOut ? 'text-gray-400' : 'text-gray-800'}`}>
                        ${opt.price.toLocaleString()}
                      </div>
                      {opt.soldOut && (
                        <span className="text-sm text-red-600 font-semibold">Sold Out</span>
                      )}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        </div>
      )}

      {/* Connection warning — only for paying users, not waitlist */}
      {!allFlightsFull && selectedFlightData && (
        <div className="bg-amber-50 border border-amber-300 rounded-lg p-5">
          <h4 className="text-base font-semibold text-amber-800 mb-3">Important: Connecting Flights from {selectedFlightData?.arrivalCity || 'Milan'}</h4>
          <p className="text-sm text-amber-900 mb-2">
            <strong>Recommended Connection Time:</strong> {selectedFlightData?.connectionWarning || 'We strongly recommend that you do not book any connecting flights departing before 2:00 PM.'}. This window allows for necessary buffer time in the event of unforeseen delays.
          </p>
          <p className="text-sm text-amber-900">
            <strong>Self-Connection Policy:</strong> Please be advised that Plan It Rite cannot accept any responsibility or liability for self-booked connecting flights. Travelers who choose to book their own separate connections do so at their own risk regarding potential delays or missed departures.
          </p>
        </div>
      )}

      {/* Travel Itinerary — shown once user has selected a flight+route */}
      {!allFlightsFull && selectedFlightData && watchRoute && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            Travel Itinerary — Jerusalem to {watchRoute.includes('newyork') ? 'New York' : selectedFlightData.arrivalCity}
          </h3>
          <div className="space-y-3 text-gray-700">
            <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-2">
              <p className="font-semibold text-blue-800">{selectedFlightData.departJerusalemDay} — Departing Jerusalem</p>
              <p className="font-semibold text-blue-800">{selectedFlightData.departTabaDay} — Flight from Taba</p>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-blue-700 whitespace-nowrap">{selectedFlightData.departJerusalemDay.split(',')[0]} {selectedFlightData.departJerusalemTime || '6:00 PM'}</span>
              <span>Bus departure from Ramada Jerusalem Hotel (Ze&apos;ev Vilnai St 6, Jerusalem)</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-blue-700 whitespace-nowrap">{selectedFlightData.departTabaDay.split(',')[0]} {selectedFlightData.departTabaTime}</span>
              <span>Flight departure from Taba</span>
            </div>
            <div className="flex items-start gap-3">
              <span className="font-semibold text-blue-700 whitespace-nowrap">{selectedFlightData.departTabaDay.split(',')[0]} {selectedFlightData.arrivalTime}</span>
              <span>Landing in {selectedFlightData.arrivalCity} (local time)</span>
            </div>
            {/* NYC connection details */}
            {watchRoute.includes('newyork') && (
              <div className="mt-3 bg-orange-50 border border-orange-200 p-4 rounded-lg">
                <h4 className="font-semibold text-orange-800 mb-2">Connecting Flight — Milan to New York</h4>
                <p className="text-sm text-orange-800">Your booking includes a connecting flight from Milan (MXP) to New York (JFK). You will receive specific flight details closer to departure.</p>
              </div>
            )}
            <div className="mt-4 bg-red-50 border border-red-200 p-3 rounded-lg">
              <p className="text-red-700 font-semibold">{selectedFlightData.connectionWarning || 'Do NOT book connecting flights before 2:00 PM'}</p>
            </div>
          </div>
        </div>
      )}

      {/* Registration Form */}
      <form onSubmit={hookFormSubmit(onSubmit)} className="space-y-6">
        {loading ? (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-logo-blue mx-auto mb-4"></div>
            <h2 className="text-xl font-semibold mb-2">Processing...</h2>
            <p className="text-gray-600">Please wait while we process your registration.</p>
          </div>
        ) : (
          <>
            {/* Route Selection — public users already selected via the vertical list above;
                access code users and waitlist users still need an inline route picker */}
            {accessCode && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Select Your Route *</h4>
                <div className="space-y-3">
                  {(!normalizedCodeRoute || normalizedCodeRoute === 'jerusalem-milan') && (
                    <label className="flex items-center gap-3 p-4 border border-gray-200 rounded-lg cursor-pointer hover:bg-blue-50 hover:border-blue-300 transition-colors">
                      <input type="radio" value="jerusalem-milan" {...register('route', { required: 'Please select a route' })} className="w-5 h-5 text-blue-600" />
                      <span className="text-gray-800 font-medium">Jerusalem &rarr; {selectedFlightData?.arrivalCity || 'Milan (MXP)'} — ${selectedFlightData?.routes?.['jerusalem-milan']?.price?.toLocaleString() || '950'}</span>
                    </label>
                  )}
                  {(!normalizedCodeRoute || normalizedCodeRoute === 'jerusalem-newyork') && (
                    <label className={`flex items-center gap-3 p-4 border rounded-lg transition-colors ${isNycFull ? 'border-gray-200 bg-gray-100 cursor-not-allowed opacity-60' : 'border-gray-200 cursor-pointer hover:bg-green-50 hover:border-green-300'}`}>
                      <input type="radio" value="jerusalem-newyork" {...register('route', { required: 'Please select a route' })} className="w-5 h-5 text-green-600" disabled={isNycFull} />
                      <span className="text-gray-800 font-medium">
                        Jerusalem &rarr; New York — ${selectedFlightData?.routes?.['jerusalem-newyork']?.price?.toLocaleString() || '1,900'}
                        {isNycFull && <span className="ml-2 text-red-600 text-sm font-semibold">(Sold Out)</span>}
                      </span>
                    </label>
                  )}
                </div>
                {normalizedCodeRoute && <p className="text-sm text-blue-600 mt-2">Your access code is restricted to this route.</p>}
                {errors.route && <p className="text-red-500 text-sm mt-2">{errors.route.message}</p>}
              </div>
            )}
            {/* Hidden route field for public users — value set by the vertical list (not required when all flights closed) */}
            {!accessCode && !allFlightsFull && <input type="hidden" {...register('route', { required: 'Please select a flight and route above' })} />}
            {!accessCode && !allFlightsFull && errors.route && <p className="text-red-500 text-sm">{errors.route.message}</p>}

            {/* Access Code display (if present) */}
            {accessCode && (
              <div className="bg-gray-50 p-6 rounded-lg">
                <h4 className="text-lg font-semibold text-gray-800 mb-4">Access Code</h4>
                <div>
                  <label className="themed-label">Organization Access Code</label>
                  <input
                    type="text"
                    value={accessCode}
                    readOnly
                    className="themed-input bg-gray-100 cursor-not-allowed"
                  />
                  <p className="text-gray-500 text-sm mt-1">You are registering with an organization-allocated seat.</p>
                </div>
              </div>
            )}

            {/* School Information Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">School Information</h4>

              <div>
                <label className="themed-label">School / Yeshiva / Organization *</label>
                {schoolsLoading ? (
                  <div className="themed-input flex items-center">
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-logo-blue mr-2"></div>
                    Loading schools...
                  </div>
                ) : schoolsError ? (
                  <div className="themed-input text-red-600">
                    Error loading schools: {schoolsError.message}. Please refresh the page.
                  </div>
                ) : (
                  <Controller
                    control={control}
                    name="school"
                    rules={{ required: 'Please select your school' }}
                    render={({ field }) => (
                      <select
                        {...field}
                        className={`themed-input ${errors.school ? 'border-red-500' : ''}`}
                        disabled={schoolsLoading}
                      >
                        {schools.map((option) => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    )}
                  />
                )}
                {errors.school && <p className="text-red-500 text-sm mt-1">{errors.school.message}</p>}
              </div>

              {/* "Other" school free-text input */}
              {watchSchool === '__other__' && (
                <div className="mt-4">
                  <label className="themed-label">School Name *</label>
                  <input
                    type="text"
                    {...register('schoolOther', {
                      validate: (value) => {
                        if (watchSchool === '__other__' && !value?.trim()) {
                          return 'Please enter your school name';
                        }
                        return true;
                      }
                    })}
                    className={`themed-input ${errors.schoolOther ? 'border-red-500' : ''}`}
                    placeholder="Enter your school name"
                  />
                  {errors.schoolOther && <p className="text-red-500 text-sm mt-1">{errors.schoolOther.message}</p>}
                </div>
              )}
            </div>

            {/* Personal Information Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Personal Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="themed-label">First Name *</label>
                  <input
                    type="text"
                    {...register('firstName', { required: 'First name is required' })}
                    className={`themed-input ${errors.firstName ? 'border-red-500' : ''}`}
                  />
                  {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName.message}</p>}
                </div>

                <div>
                  <label className="themed-label">Last Name *</label>
                  <input
                    type="text"
                    {...register('lastName', { required: 'Last name is required' })}
                    className={`themed-input ${errors.lastName ? 'border-red-500' : ''}`}
                  />
                  {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName.message}</p>}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="themed-label">Date of Birth *</label>
                  <input
                    type="text"
                    {...register('birthDate', {
                      required: 'Date of birth is required',
                      pattern: {
                        value: /^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/,
                        message: 'Please enter a valid date in DD/MM/YYYY format'
                      }
                    })}
                    className={`themed-input ${errors.birthDate ? 'border-red-500' : ''}`}
                    placeholder="DD/MM/YYYY (e.g., 15/12/2003)"
                    maxLength="10"
                    onChange={(e) => {
                      const value = e.target.value;
                      const numbers = value.replace(/\D/g, '');

                      let formatted = '';
                      if (numbers.length <= 2) {
                        formatted = numbers;
                      } else if (numbers.length <= 4) {
                        formatted = `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
                      } else {
                        formatted = `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
                      }

                      e.target.value = formatted;
                      e.target.classList.remove('border-red-500', 'bg-red-50');
                    }}
                  />
                  {errors.birthDate && <p className="text-red-500 text-sm mt-1">{errors.birthDate.message}</p>}
                  <p className="text-gray-500 text-sm mt-1">Format: DD/MM/YYYY</p>
                </div>

                <div>
                  <label className="themed-label">Gender *</label>
                  <select
                    {...register('gender', { required: 'Gender is required' })}
                    className={`themed-input ${errors.gender ? 'border-red-500' : ''}`}
                  >
                    <option value="">Select...</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                  {errors.gender && <p className="text-red-500 text-sm mt-1">{errors.gender.message}</p>}
                </div>
              </div>
            </div>

            {/* Contact Information Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Contact Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="themed-label">Email *</label>
                  <input
                    type="email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                        message: 'Invalid email format'
                      }
                    })}
                    className={`themed-input ${errors.email ? 'border-red-500' : ''}`}
                  />
                  {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <label className="themed-label">Phone Number *</label>
                  <input
                    type="tel"
                    {...register('phone', { required: 'Phone number is required' })}
                    className={`themed-input ${errors.phone ? 'border-red-500' : ''}`}
                    placeholder="e.g., +1-555-123-4567"
                  />
                  {errors.phone && <p className="text-red-500 text-sm mt-1">{errors.phone.message}</p>}
                </div>
              </div>
            </div>

            {/* Travel / Passport Information Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Passport Information</h4>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="themed-label">Passport Number *</label>
                  <input
                    type="text"
                    {...register('passportNumber', { required: 'Passport number is required' })}
                    className={`themed-input ${errors.passportNumber ? 'border-red-500' : ''}`}
                    placeholder="Enter passport number"
                  />
                  {errors.passportNumber && <p className="text-red-500 text-sm mt-1">{errors.passportNumber.message}</p>}
                </div>

                <div>
                  <label className="themed-label">Passport Country *</label>
                  <input
                    type="text"
                    {...register('passportCountry', { required: 'Passport country is required' })}
                    className={`themed-input ${errors.passportCountry ? 'border-red-500' : ''}`}
                    placeholder="e.g., United States"
                  />
                  {errors.passportCountry && <p className="text-red-500 text-sm mt-1">{errors.passportCountry.message}</p>}
                </div>
              </div>

              <div className="mt-4">
                <label className="themed-label">Passport Expiration Date *</label>
                <input
                  type="date"
                  {...register('passportExpiration', { required: 'Passport expiration date is required' })}
                  className={`themed-input ${errors.passportExpiration ? 'border-red-500' : ''}`}
                />
                {errors.passportExpiration && <p className="text-red-500 text-sm mt-1">{errors.passportExpiration.message}</p>}
              </div>

              <div className="mt-4">
                <label className="themed-label">Upload Passport *</label>
                <input
                  type="file"
                  id="passportFile"
                  name="passportFile"
                  accept="image/*,application/pdf"
                  onChange={handlePassportFileChange}
                  className={`themed-input ${isSubmitted && !passportFileName ? 'border-red-500' : ''}`}
                />
                {passportFileName && (
                  <p className="text-secondary-blue text-sm mt-1">
                    File selected: {passportFileName}
                  </p>
                )}
                {isSubmitted && !passportFileName && (
                  <p className="text-red-500 text-sm mt-1">Please upload your passport</p>
                )}
                <p className="text-gray-500 text-xs mt-1">
                  Maximum 25 MB. Accepted formats: images (JPG, PNG) and PDF.
                </p>
              </div>
            </div>

            {/* Infant Section */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('hasInfant')}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                  disabled={isInfantFull && !watchHasInfant}
                />
                <div>
                  <span className="text-gray-800 font-semibold">
                    Traveling with a lap infant under 2? (+${selectedFlightData?.infantPrice || 200})
                  </span>
                  <p className="text-gray-500 text-sm mt-1">Lap infant only — child must be under 2 years old on the date of the flight. No separate seat provided.</p>
                  {isInfantFull && !watchHasInfant && (
                    <p className="text-red-600 text-sm mt-1">Infant capacity for this flight has been reached.</p>
                  )}
                </div>
              </label>

              {watchHasInfant && (
                <div className="mt-4 border-t border-gray-200 pt-4 space-y-4">
                  <h4 className="text-base font-semibold text-gray-800">Infant Details</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="themed-label">Infant First Name *</label>
                      <input
                        type="text"
                        {...register('infantFirstName', { validate: v => !watchHasInfant || v?.trim() ? true : 'Required' })}
                        className={`themed-input ${errors.infantFirstName ? 'border-red-500' : ''}`}
                      />
                      {errors.infantFirstName && <p className="text-red-500 text-sm mt-1">{errors.infantFirstName.message}</p>}
                    </div>
                    <div>
                      <label className="themed-label">Infant Last Name *</label>
                      <input
                        type="text"
                        {...register('infantLastName', { validate: v => !watchHasInfant || v?.trim() ? true : 'Required' })}
                        className={`themed-input ${errors.infantLastName ? 'border-red-500' : ''}`}
                      />
                      {errors.infantLastName && <p className="text-red-500 text-sm mt-1">{errors.infantLastName.message}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="themed-label">Infant Date of Birth *</label>
                      <input
                        type="text"
                        {...register('infantBirthDate', {
                          validate: v => {
                            if (!watchHasInfant) return true;
                            if (!v?.trim()) return 'Required';
                            // Validate format
                            if (!/^(0[1-9]|[12][0-9]|3[01])\/(0[1-9]|1[0-2])\/\d{4}$/.test(v)) return 'DD/MM/YYYY format';
                            // Validate infant is under 2 on the flight date
                            const [dd, mm, yyyy] = v.split('/').map(Number);
                            const dob = new Date(yyyy, mm - 1, dd);
                            // Use a generous flight date (March 31 2026) so infants close to 2 aren't wrongly rejected on the frontend
                            const flightDate = new Date(2026, 2, 31);
                            const ageMs = flightDate - dob;
                            const twoYearsMs = 2 * 365.25 * 24 * 60 * 60 * 1000;
                            if (ageMs >= twoYearsMs) return 'Infant must be under 2 years old on the date of the flight';
                            if (ageMs < 0) return 'Date of birth cannot be in the future';
                            return true;
                          }
                        })}
                        className={`themed-input ${errors.infantBirthDate ? 'border-red-500' : ''}`}
                        placeholder="DD/MM/YYYY"
                        maxLength="10"
                        onChange={(e) => {
                          const numbers = e.target.value.replace(/\D/g, '');
                          let formatted = '';
                          if (numbers.length <= 2) formatted = numbers;
                          else if (numbers.length <= 4) formatted = `${numbers.slice(0, 2)}/${numbers.slice(2)}`;
                          else formatted = `${numbers.slice(0, 2)}/${numbers.slice(2, 4)}/${numbers.slice(4, 8)}`;
                          e.target.value = formatted;
                        }}
                      />
                      {errors.infantBirthDate && <p className="text-red-500 text-sm mt-1">{errors.infantBirthDate.message}</p>}
                    </div>
                    <div>
                      <label className="themed-label">Infant Gender *</label>
                      <select
                        {...register('infantGender', { validate: v => !watchHasInfant || v ? true : 'Required' })}
                        className={`themed-input ${errors.infantGender ? 'border-red-500' : ''}`}
                      >
                        <option value="">Select...</option>
                        <option value="Male">Male</option>
                        <option value="Female">Female</option>
                      </select>
                      {errors.infantGender && <p className="text-red-500 text-sm mt-1">{errors.infantGender.message}</p>}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="themed-label">Infant Passport Number *</label>
                      <input
                        type="text"
                        {...register('infantPassportNumber', { validate: v => !watchHasInfant || v?.trim() ? true : 'Required' })}
                        className={`themed-input ${errors.infantPassportNumber ? 'border-red-500' : ''}`}
                      />
                      {errors.infantPassportNumber && <p className="text-red-500 text-sm mt-1">{errors.infantPassportNumber.message}</p>}
                    </div>
                    <div>
                      <label className="themed-label">Infant Passport Country *</label>
                      <input
                        type="text"
                        {...register('infantPassportCountry', { validate: v => !watchHasInfant || v?.trim() ? true : 'Required' })}
                        className={`themed-input ${errors.infantPassportCountry ? 'border-red-500' : ''}`}
                        placeholder="e.g., United States"
                      />
                      {errors.infantPassportCountry && <p className="text-red-500 text-sm mt-1">{errors.infantPassportCountry.message}</p>}
                    </div>
                  </div>
                  <div>
                    <label className="themed-label">Infant Passport Expiration *</label>
                    <input
                      type="date"
                      {...register('infantPassportExpiration', { validate: v => !watchHasInfant || v ? true : 'Required' })}
                      className={`themed-input ${errors.infantPassportExpiration ? 'border-red-500' : ''}`}
                    />
                    {errors.infantPassportExpiration && <p className="text-red-500 text-sm mt-1">{errors.infantPassportExpiration.message}</p>}
                  </div>
                </div>
              )}
            </div>

            {/* Terms & Conditions Checkbox */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('termsAccepted', { required: 'You must agree to the Terms & Conditions' })}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <span className="text-gray-800">
                  I have read and agree to the <button type="button" onClick={() => setShowTermsModal(true)} className="text-blue-600 hover:text-blue-800 underline font-semibold">Terms & Conditions</button> *
                </span>
              </label>
              {errors.termsAccepted && <p className="text-red-500 text-sm mt-2">{errors.termsAccepted.message}</p>}
            </div>

            {/* Commit to Book checkbox (shown when flight is full / waitlist mode) */}
            {allFlightsFull && (
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-6">
                <label className="flex items-start gap-3 cursor-pointer">
                  <input
                    type="checkbox"
                    {...register('commitToBook')}
                    className="mt-1 w-5 h-5 text-green-600 rounded"
                  />
                  <div>
                    <span className="text-green-900 font-semibold">
                      I commit to booking if you get back to me within 24 hours
                    </span>
                    <p className="text-green-800 text-sm mt-1">
                      By checking this box, you indicate that if we contact you within 24 hours with availability, you are ready to complete your booking. Registrants who commit will be given precedence.
                    </p>
                  </div>
                </label>
              </div>
            )}

            {/* Sharm el-Sheikh Interest — hidden, Sharm is now the default route */}
            {/* <div className="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <label className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  {...register('interestedSharmElSheikh')}
                  className="mt-1 w-5 h-5 text-blue-600 rounded"
                />
                <div>
                  <span className="text-blue-900 font-semibold">
                    If we have an option via Sharm el-Sheikh with full security, would you be interested?
                  </span>
                </div>
              </label>
            </div> */}

            {/* Submit Button */}
            <div className="text-center">
              <button
                type="submit"
                disabled={isSubmitting || loading}
                className="w-full max-w-md mx-auto py-3 px-6 rounded-lg font-semibold text-lg bg-logo-blue hover:bg-blue-700 text-white disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {isSubmitting || loading ? 'Submitting...' : (allFlightsFull ? 'Register' : 'Register & Proceed to Payment')}
              </button>
            </div>
          </>
        )}
      </form>
      <TermsModal isOpen={showTermsModal} onClose={() => setShowTermsModal(false)} />
    </div>
  )
}

FlightRegistrationForm.propTypes = {
  onSubmitSuccess: PropTypes.func
}

export default FlightRegistrationForm